/*
 * Monfiora — cart controller for the drawer and for /cart ("Build your set", Phase 1 2026-10-04,
 * Phase 2 2026-10-05). Loaded deferred, while cart_type is "drawer", after assets/cart-drawer.js (not on
 * /cart) and before assets/lp-free-gift.js (layout/theme.liquid).
 *
 * One surface per page:
 *   drawer  <cart-drawer> (every page but /cart): renders through its update() / refresh()
 *   page    [data-mf-cart-page] on /cart (sections/main-cart-items, Cart type Drawer): swaps
 *           #main-cart-items .js-contents and #main-cart-footer .js-contents from a Section Rendering
 *           response, like Dawn's assets/cart.js, and the header bubble
 * Claim, remove, change scent and swap are delegated from the surface's root. On /cart a capture-phase
 * listener on [data-mf-cart-page] keeps a strip radio's change from Dawn's <cart-items>, which would
 * read it as a quantity edit and post /cart/change.js with no line (the drawer's twin is in
 * assets/cart-drawer.js). Dawn's stepper and paid Remove render /cart themselves (assets/cart.js); on
 * its cartUpdate (source 'cart-items') the page says what changed in its own words ("Cart updated.
 * Total {total}.", or the offer line that changed) and puts focus back where Dawn left none.
 *
 * window.MonfioraCart — the one way other theme scripts (the builder) reach the cart:
 *   sectionIds()                      the surface's section ids, or [] with no surface
 *   openDrawer(addJson, {opener, focusKey, message, kind})
 *                                     render an /cart/add.js response that carried those sections and open
 *                                     (kind 'set': announce "Set added" even for a one-line set)
 *   refresh({open, opener, focusKey, message, kind}) GET ?sections= (no Accept header) and render
 *   add(items, {focusKey, message})   POST /cart/add.js, render, keep the drawer open
 *   claimFree(variantId)              add one free piece: properties {_free_gift:'true', _set_id:<new>}
 *   removeLine(key)                   POST /cart/change.js {id:key, quantity:0}, render
 *   swapFree(lineKey, variantId, {opener})
 *                                     change the free piece's scent: add the new piece (no sections), read
 *                                     the cart, then remove the old line by its current key (the add
 *                                     re-keys it) with sections, one render; the set keeps its
 *                                     _set_id. Runs inside MonfioraFreeGift.hold(), so lp-free-gift.js
 *                                     neither paints nor removes the short-lived second piece, and a Check
 *                                     out pressed meanwhile waits. A failure before the remove leaves the
 *                                     cart exactly as it was.
 *   note(text)                        the one-time total-check note under Total (the next render clears it)
 *   isOpen(), syncLock()
 *   event 'monfiora:cart:updated' {source} on document after every render
 *
 * The drawer's pinned footer carries data-overflow (its shadow, assets/monfiora-cart-drawer.css) while
 * the body has content below its fold: checked after every render, when the drawer opens, on resize and
 * scroll, and when the body's content changes height (a change-scent strip opens, an error line appears).
 *
 * Every write goes through window.fetch at call time with a JSON string body, so lp-free-gift.js's
 * network hook and Shopify's own add-to-cart listener hear it. A write waits while a Dawn quantity
 * change is pending or running (Dawn edits lines by number 300 ms after the last tap; a line added or
 * removed first would shift its target). Labels, totals and the offer panel are never painted here: the
 * server render is the truth. All customer-facing strings come from data attributes the Liquid renders.
 */
(function () {
  'use strict';
  if (window.MonfioraCart) return;

  var DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  var QTY_SETTLE_MS = 350;      // Dawn sends a quantity change 300 ms after the last tap or blur
  var SETTLE_MAX_MS = 8000;     // a write never waits longer than this
  var SWAP_RETRY_MS = 800;      // the swap's remove is tried once more after this
  var SWAP_RETRY_429_MS = 2000; // … or this, after a rate limit
  var LIVE_DELAY_MS = 150;      // a beat after the swap, so the status node is registered before it speaks
  var busyButtons = [];
  var lastQtyTouch = 0;         // the last real tap/typing on a stepper
  var swapping = false;         // one swap at a time
  var pageIntent = null;        // /cart: the control last used, for focus after a render
  var pageLiveTimer = null;
  var lastPageState = '';       // /cart: the offer state before the customer's last tap (or the last render)

  function drawer() {
    var d = document.querySelector('cart-drawer');
    return d && typeof d.update === 'function' ? d : null;
  }
  function pageRoot() { return document.querySelector('[data-mf-cart-page]'); }
  function route(name, fallback) { return (window.routes && window.routes[name]) || fallback; }
  function cartUrl() { return route('cart_url', '/cart'); }
  function newSetId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
  function quiet(fn) {
    var fg = window.MonfioraFreeGift;
    if (fg && typeof fg.quiet === 'function') fg.quiet(fn); else fn();
  }
  function parse(html) { return new DOMParser().parseFromString(html, 'text/html'); }

  /* ---------- surfaces ---------- */
  function pageIds() {
    var ids = [];
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (items && items.dataset && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset && footer.dataset.id) ids.push(footer.dataset.id);
    ids.push('cart-icon-bubble');
    return ids;
  }
  function surface() {
    var d = drawer();
    if (d) {
      return {
        kind: 'drawer', root: d, ids: DRAWER_SECTIONS.slice(),
        host: function () { return document.getElementById('CartDrawer-CartItems'); },
        errors: function () { return document.getElementById('CartDrawer-CartErrors'); },
        render: function (sections, opts) { quiet(function () { d.update(sections, opts); }); return Promise.resolve(); },
        refresh: function (opts) { return d.refresh(opts); }
      };
    }
    var p = pageRoot();
    if (p) {
      return {
        kind: 'page', root: p, ids: pageIds(),
        host: function () { return document.getElementById('main-cart-items'); },
        errors: function () { return document.getElementById('cart-errors'); },
        render: function (sections, opts) { swapPage(sections, opts); return Promise.resolve(); },
        refresh: refreshPage
      };
    }
    return null;
  }
  function sectionIds() { var s = surface(); return s ? s.ids : []; }
  function hasRender(json, s) {
    if (!json || !json.sections) return false;
    return s.kind === 'drawer' ? !!json.sections['cart-drawer'] : !!json.sections[s.ids[0]];
  }

  // Resolves once no Dawn quantity change is pending (QTY_SETTLE_MS after the last stepper tap) or
  // running (its .cart__items--disabled), or lp-free-gift.js is removing a line (it sets the same class).
  function linesHost() {
    return document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items');
  }
  function whenLinesSettled() {
    var deadline = Date.now() + SETTLE_MAX_MS;
    return new Promise(function (resolve) {
      (function check() {
        if (Date.now() >= deadline) return resolve();
        var wait = QTY_SETTLE_MS - (Date.now() - lastQtyTouch);
        if (wait > 0) return setTimeout(check, wait + 10);
        var host = linesHost();
        if (host && host.classList.contains('cart__items--disabled')) return setTimeout(check, 60);
        resolve();
      })();
    });
  }

  // settled: the caller has already waited (and may have marked the lines busy itself).
  function post(url, body, settled) {
    if (settled) return send(url, body);
    return whenLinesSettled().then(function () { return send(url, body); });
  }
  function send(url, body) {
    return window.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (json) {
        if (!res.ok || !json || (json.status && !json.items)) {
          var err = new Error((json && (json.description || json.message)) || 'HTTP ' + res.status);
          err.status = res.status;
          err.json = json;
          throw err;
        }
        return json;
      });
    });
  }
  function readCart() {
    return window.fetch(route('cart_url', '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) {
        var err = new Error('HTTP ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json();
    }).then(function (cart) {
      if (!cart || !cart.items) throw new Error('not a cart');
      return cart;
    });
  }

  function openDrawer(addJson, opts) {
    opts = opts || {};
    var d = drawer();
    if (!d) {
      if (pageRoot()) return render(addJson, { focusKey: opts.focusKey, message: opts.message, source: 'add' });
      window.location.href = cartUrl();
      return Promise.resolve(null);
    }
    if (!addJson || !addJson.sections || !addJson.sections['cart-drawer']) {
      return refresh({ open: true, opener: opts.opener, focusKey: opts.focusKey, message: opts.message, kind: opts.kind, reset: true, source: 'add' });
    }
    quiet(function () {
      d.renderContents(addJson, { opener: opts.opener, focusKey: opts.focusKey, message: opts.message, kind: opts.kind });
    });
    return Promise.resolve(addJson);
  }

  function refresh(opts) {
    opts = opts || {};
    var s = surface();
    if (!s) {
      if (opts.open) window.location.href = cartUrl();
      return Promise.resolve(null);
    }
    return s.refresh(opts);
  }

  // Render a write's response (with sections) on the surface; read the sections if they are missing.
  function render(json, opts) {
    var s = surface();
    if (!s) { window.location.href = cartUrl(); return Promise.resolve(json); }
    if (hasRender(json, s)) {
      if (json && typeof json.item_count === 'number') opts = Object.assign({ itemCount: json.item_count }, opts);
      return s.render(json.sections, opts).then(function () { return json; });
    }
    return s.refresh(opts).then(function () { return json; }, function () { return json; });
  }

  function add(items, opts) {
    opts = opts || {};
    return post(route('cart_add_url', '/cart/add') + '.js', { items: items, sections: sectionIds(), sections_url: window.location.pathname })
      .then(function (json) {
        if (typeof opts.afterPost === 'function') opts.afterPost(json);
        var focusKey = opts.focusKey;
        var fallback;
        if (focusKey === 'line-new') {
          // The new line's name: its key is in the response (a free piece or a design new to the cart is
          // always a line of its own); else the first line, where Shopify lists the newest.
          var first = json.items && json.items[0];
          focusKey = first && first.key ? 'line-' + first.key : null;
          fallback = 'first-line';
        }
        return render(json, {
          open: true, opener: opts.opener, focusKey: focusKey, fallback: fallback,
          message: opts.message, source: opts.source || 'add', reset: true
        });
      });
  }

  // lp-free-gift.js blocks claims for a while after one came back charged (the 5% won): adding another
  // would only be removed again.
  function claimsBlocked() {
    var fg = window.MonfioraFreeGift;
    return !!(fg && typeof fg.claimBlocked === 'function' && fg.claimBlocked());
  }

  function claimFree(variantId, opts) {
    opts = opts || {};
    if (claimsBlocked()) {
      var err = new Error('claims blocked');
      err.blocked = true;
      return Promise.reject(err);
    }
    var properties = { _free_gift: 'true', _set_id: newSetId() };
    return add([{ id: Number(variantId), quantity: 1, properties: properties }], {
      focusKey: 'line-new', message: opts.message, source: 'claim', opener: opts.opener,
      // Tell lp-free-gift.js a claim landed, as its own claim does: if Shopify charged it, claims block.
      afterPost: function () {
        var fg = window.MonfioraFreeGift;
        if (fg && typeof fg.markClaim === 'function') fg.markClaim();
      }
    });
  }

  // Set a line (by key) to a quantity and render; removeLine is the quantity-0 case.
  function changeLine(key, quantity, opts) {
    opts = opts || {};
    return post(route('cart_change_url', '/cart/change') + '.js', {
      id: key, quantity: quantity, sections: sectionIds(), sections_url: window.location.pathname
    }, opts.settled).then(function (json) { return render(json, { source: 'remove', focusKey: opts.focusKey }); });
  }
  function removeLine(key, opts) { return changeLine(key, 0, opts); }

  // The Check out hold (assets/lp-free-gift.js) is running: the cart is being checked for checkout.
  function checkoutHeld() {
    return !!document.querySelector('#CartDrawer-Checkout[aria-busy="true"], #checkout[aria-busy="true"]');
  }

  function note(text) {
    var el = document.querySelector('cart-drawer [data-mf-check-note]');
    if (el) el.textContent = text || '';
  }

  function isOpen() {
    var d = document.querySelector('cart-drawer');
    return !!(d && d.classList.contains('active'));
  }

  // body.overflow-hidden only while the drawer or a Dawn modal is open; released in every other case
  // (close, Esc, scrim, a modal closing over the drawer, a back/forward-cache return).
  function modalOpen() {
    return !!document.querySelector('modal-dialog[open], quick-add-modal[open], details-modal details[open]');
  }
  function syncLock() {
    if (!document.body) return;
    document.body.classList.toggle('overflow-hidden', isOpen() || modalOpen());
  }

  /* ---------- /cart: the page surface ---------- */
  function lineOf(el) { return el && el.closest ? el.closest('.monfiora-line') : null; }
  function notePageIntent(target) {
    var root = pageRoot();
    if (!root || !target || !target.closest || !root.contains(target)) return;
    var keyed = target.closest('[data-mf-focus]');
    var line = lineOf(target);
    pageIntent = {
      key: keyed ? keyed.getAttribute('data-mf-focus') : null,
      lineIndex: line ? Array.prototype.indexOf.call(root.querySelectorAll('.monfiora-line'), line) : -1
    };
  }
  function pageState(root) {
    var progress = root.querySelector('.monfiora-progress');
    return progress ? progress.getAttribute('data-state') || '' : '';
  }
  function byFocusKey(scope, key) {
    if (!key || !scope) return null;
    return scope.querySelector('[data-mf-focus="' + String(key).replace(/["\\]/g, '\\$&') + '"]');
  }
  // The same chain as the drawer: the key, a fallback key, the line at the same position (else the one
  // before it), then the heading (the empty state's when the cart emptied).
  function pageFocusTarget(root, key, fallback, lineIndex, empty) {
    var el = byFocusKey(root, key);
    if (!el && fallback && fallback !== 'first-line') el = byFocusKey(root, fallback);
    var lines = root.querySelectorAll('.monfiora-line');
    if (!el && fallback === 'first-line' && lines[0]) el = lines[0].querySelector('.cart-item__name');
    if (!el && key && lineIndex >= 0) {
      var line = lines[lineIndex] || lines[lineIndex - 1];
      el = line ? line.querySelector('.cart-item__name') : null;
    }
    if (!el && key) el = byFocusKey(root, empty ? 'empty' : 'heading');
    return el;
  }
  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: false }); } catch (e) { el.focus(); }
  }
  // The page's status node (sections/main-cart-items, outside .js-contents). Not a bare [data-mf-live]:
  // the offer panel (section.monfiora-progress) carries data-mf-live too, as the line said here, and
  // comes first in the page, so a bare lookup would write over the panel after every render.
  function announcePage(text) {
    if (!text) return;
    clearTimeout(pageLiveTimer);
    pageLiveTimer = setTimeout(function () {
      var region = document.querySelector('[data-mf-cart-page] p[role="status"][data-mf-live]');
      if (region) region.textContent = text;
    }, LIVE_DELAY_MS);
  }
  // One message per render: an explicit one, else the offer state that changed, else the new total.
  function pageMessage(root, prevState, opts) {
    if (opts.message) return opts.message;
    var body = root.querySelector('.monfiora-cartpage__body');
    if (!body) return '';
    var progress = root.querySelector('.monfiora-progress');
    var state = pageState(root);
    var stateLine = progress && state !== prevState ? progress.getAttribute('data-mf-live') || '' : '';
    return stateLine || body.getAttribute('data-mf-live-total') || '';
  }

  // A Section Rendering response for /cart ({<items id>: html, <footer id>: html, 'cart-icon-bubble': html}).
  function swapPage(sections, opts) {
    opts = opts || {};
    var root = pageRoot();
    if (!root || !sections) return;
    var active = document.activeElement;
    if (active && active !== document.body && root.contains(active)) notePageIntent(active);
    var intent = pageIntent || { key: null, lineIndex: -1 };
    var prevState = pageState(root);
    var swapped = false;
    quiet(function () {
      ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
        var host = document.getElementById(id);
        if (!host || !host.dataset || !host.dataset.id) return;
        var html = sections[host.dataset.id];
        if (!html) return;
        var src = parse(html).querySelector('.js-contents');
        var dst = host.querySelector('.js-contents');
        if (src && dst) { dst.innerHTML = src.innerHTML; swapped = true; }
      });
      var bubbleHtml = sections['cart-icon-bubble'];
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && bubbleHtml) {
        var srcBubble = parse(bubbleHtml).querySelector('.shopify-section');
        if (srcBubble) bubble.innerHTML = srcBubble.innerHTML;
      }
    });
    if (!swapped) return;
    // A swap's "couldn’t confirm" alert sits in #cart-errors, outside both .js-contents: a later render
    // clears it (unknown() appends its own after its refresh).
    clearError(document.getElementById('cart-errors'), 'monfiora-line__swap-error');
    var empty = typeof opts.itemCount === 'number' ? opts.itemCount === 0 : !root.querySelector('#main-cart-items .monfiora-line');
    var items = root.querySelector('cart-items');
    if (items) items.classList.toggle('is-empty', empty);
    var footer = document.getElementById('main-cart-footer');
    if (footer) footer.classList.toggle('is-empty', empty);
    // Adds focus what they ask for (the new line); edits go back to the control used.
    var kept = !opts.focusKey && active && active !== document.body && active.isConnected && root.contains(active);
    if (!kept) {
      var key = opts.focusKey || (opts.reset ? null : intent.key);
      focusEl(pageFocusTarget(root, key, opts.fallback, intent.lineIndex, empty));
    }
    if (opts.announce !== false) announcePage(pageMessage(root, prevState, opts));
    lastPageState = pageState(root);
    document.dispatchEvent(new CustomEvent('monfiora:cart:updated', { detail: { source: opts.source || 'page' } }));
  }

  // Dawn's stepper or paid Remove rendered /cart (assets/cart.js publishes cartUpdate, source
  // 'cart-items', after its swap and its own focus). The page's live region says it as our renders do;
  // when Dawn left focus nowhere (a Remove: its control is gone), it goes to the line now in that place,
  // else the one before it, else the empty state's heading.
  function onDawnRender(root, event) {
    if (!event || event.source !== 'cart-items' || !root.isConnected) return;
    if (event.cartData && event.cartData.errors) return;
    clearError(document.getElementById('cart-errors'), 'monfiora-line__swap-error');
    var data = event.cartData || {};
    var empty = typeof data.item_count === 'number' ? data.item_count === 0 : !root.querySelector('#main-cart-items .monfiora-line');
    var active = document.activeElement;
    if (!active || active === document.body || active.isConnected === false) {
      var intent = pageIntent || { key: null, lineIndex: -1 };
      focusEl(pageFocusTarget(root, intent.key, null, intent.lineIndex, empty));
    }
    announcePage(pageMessage(root, lastPageState, {}));
    lastPageState = pageState(root);
  }

  // GET /cart?sections= (no Accept header) and swap.
  function refreshPage(opts) {
    opts = opts || {};
    var ids = pageIds();
    return window.fetch(window.location.pathname + '?sections=' + ids.join(','))
      .then(function (res) { return res.ok ? res.json() : Promise.reject(new Error('HTTP ' + res.status)); })
      .then(function (sections) { swapPage(sections, opts); return sections; });
  }

  /* ---------- controls (event delegation on the surface's root) ---------- */
  function setBusy(btn, on, label) {
    if (!btn) return;
    var slot = btn.querySelector('[data-mf-label]');
    if (on) {
      if (slot && btn.__mfLabel == null) btn.__mfLabel = slot.innerHTML;
      if (slot && label) slot.textContent = label;
      // A named button says the busy label too (its aria-label would otherwise still name the action).
      if (label && btn.hasAttribute('aria-label') && btn.__mfAria == null) {
        btn.__mfAria = btn.getAttribute('aria-label');
        btn.setAttribute('aria-label', label);
      }
      btn.setAttribute('aria-busy', 'true');
      if (busyButtons.indexOf(btn) < 0) busyButtons.push(btn);
    } else {
      if (slot && btn.__mfLabel != null) slot.innerHTML = btn.__mfLabel;
      btn.__mfLabel = null;
      if (btn.__mfAria != null) btn.setAttribute('aria-label', btn.__mfAria);
      btn.__mfAria = null;
      btn.removeAttribute('aria-busy');
      busyButtons = busyButtons.filter(function (b) { return b !== btn; });
    }
  }
  function isBusy(btn) { return !!btn && btn.getAttribute('aria-busy') === 'true'; }
  function clearError(scope, cls) {
    if (!scope) return;
    [].forEach.call(scope.querySelectorAll('.' + cls), function (n) { n.remove(); });
  }
  // Errors are inserted as new role=alert nodes: an alert that only changes text is not always spoken.
  function showError(anchor, cls, text) {
    if (!anchor || !anchor.parentNode || !text) return;
    var p = document.createElement('p');
    p.className = cls;
    p.setAttribute('role', 'alert');
    p.textContent = text;
    anchor.parentNode.insertBefore(p, anchor.nextSibling);
  }
  function appendError(container, cls, text) {
    if (!container || !text) return null;
    var p = document.createElement('p');
    p.className = cls;
    p.setAttribute('role', 'alert');
    p.textContent = text;
    container.appendChild(p);
    return p;
  }
  // A free-piece tile Shopify refused (sold out): it can no longer be picked, and says so.
  function markSoldOut(input) {
    input.disabled = true;
    input.checked = false;
    var label = input.getAttribute('aria-label');
    if (label) input.setAttribute('aria-label', label.replace(/, free$/, ', sold out'));
    var tile = input.closest('.monfiora-freetile');
    if (tile) tile.classList.add('monfiora-freetile--soldout');
  }

  function onClaim(btn) {
    if (isBusy(btn)) return;
    var panel = btn.closest('.monfiora-progress');
    var strip = panel && panel.querySelector('[data-mf-strip]');
    if (!panel || !strip) return;
    clearError(panel, 'monfiora-freestrip__error');
    var input = strip.querySelector('input[type="radio"]:checked:not(:disabled)');
    if (!input) { showError(btn, 'monfiora-freestrip__error', panel.getAttribute('data-mf-refused')); return; }
    // The free-gift controller read the same cart this panel was rendered from and found a free piece in
    // it already (a stale panel): never add another; re-render instead.
    var fg = window.MonfioraFreeGift;
    var st = fg && fg.getState ? fg.getState() : null;
    var seen = fg && fg.getCart ? fg.getCart() : null;
    var sigEl = btn.closest('[data-cart-sig]');
    var sameCart = !!(seen && sigEl && sigEl.getAttribute('data-cart-sig') === seen.item_count + ':' + seen.total_price);
    if (st && sameCart && (st.giftUnits > 0 || st.freeTotal > 0)) { refresh({ source: 'claim' }).catch(function () {}); return; }
    if (claimsBlocked()) { showError(btn, 'monfiora-freestrip__error', panel.getAttribute('data-mf-blocked')); return; }
    setBusy(btn, true, panel.getAttribute('data-mf-busy'));
    strip.setAttribute('inert', '');
    claimFree(input.value, { message: input.getAttribute('data-mf-added') }).catch(function (err) {
      if (!btn.isConnected) return;
      setBusy(btn, false);
      strip.removeAttribute('inert');
      if (err && err.blocked) {
        showError(btn, 'monfiora-freestrip__error', panel.getAttribute('data-mf-blocked'));
      } else if (err && err.status === 422) {
        // Refused (sold out): that tile can no longer be picked; the next one in stock is chosen.
        markSoldOut(input);
        var next = strip.querySelector('input[type="radio"]:not(:disabled)');
        if (next) next.checked = true;
        showError(btn, 'monfiora-freestrip__error', panel.getAttribute('data-mf-refused'));
      } else {
        showError(btn, 'monfiora-freestrip__error', panel.getAttribute('data-mf-fail'));
      }
    });
  }

  function onSuggest(btn) {
    if (isBusy(btn)) return;
    var section = btn.closest('.monfiora-suggest');
    var row = btn.closest('[data-mf-suggest-row]');
    if (!section || !row) return;
    clearError(section, 'monfiora-suggest__error');
    setBusy(btn, true, section.getAttribute('data-mf-busy'));
    row.setAttribute('inert', '');
    add([{ id: Number(btn.getAttribute('data-variant-id')), quantity: 1 }], { focusKey: 'line-new', source: 'suggestion' })
      .catch(function () {
        if (!btn.isConnected) return;
        setBusy(btn, false);
        row.removeAttribute('inert');
        var p = document.createElement('p');
        p.className = 'monfiora-suggest__error';
        p.setAttribute('role', 'alert');
        p.textContent = section.getAttribute('data-mf-fail') || '';
        row.appendChild(p);
      });
  }

  function onRemove(btn) {
    if (isBusy(btn)) return;
    var key = btn.getAttribute('data-mf-remove-line');
    if (!key) return;
    // The W panel takes one unit off (data-mf-remove-to); the free line's Remove takes the line.
    var to = parseInt(btn.getAttribute('data-mf-remove-to'), 10);
    if (isNaN(to) || to < 0) to = 0;
    setBusy(btn, true);
    var host = null;
    whenLinesSettled().then(function () {
      host = linesHost();
      if (host) host.classList.add('cart__items--disabled');   // lp-free-gift.js waits while the lines change
      return changeLine(key, to, { settled: true });
    }).catch(function () {
      var s = surface();
      var errors = s && s.errors();
      if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
    }).then(function () {
      if (btn.isConnected) setBusy(btn, false);
      var h = linesHost();
      if (h) h.classList.remove('cart__items--disabled');
      if (host && host !== h) host.classList.remove('cart__items--disabled');
    });
  }

  // "Swap to {Scent}" names the picked tile, and shows only while that tile is not the line's own scent
  // (the rendered, default choice): offering a swap to the scent the line already has reads wrong.
  function syncSwap(panel) {
    var picked = panel.querySelector('input[type="radio"]:checked');
    var swap = panel.querySelector('[data-mf-swap]');
    var name = panel.querySelector('[data-mf-swap-name]');
    if (name && picked && !isBusy(swap)) name.textContent = picked.getAttribute('data-mf-scent') || '';
    if (swap && picked && picked.getAttribute('data-mf-swap-label') && !isBusy(swap)) {
      swap.setAttribute('aria-label', picked.getAttribute('data-mf-swap-label'));
    }
    if (swap) swap.hidden = !picked || picked.defaultChecked;
  }

  function onChangeToggle(btn) {
    var panel = document.getElementById(btn.getAttribute('aria-controls'));
    if (!panel) return;
    var open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    if (open) {
      syncSwap(panel);
      var checked = panel.querySelector('input[type="radio"]:checked');
      if (checked) scrollTile(checked);
    }
    queueOverflow();
  }

  function onKeep(btn) {
    var panel = btn.closest('.monfiora-line__change-panel');
    if (!panel || panel.hasAttribute('aria-busy')) return;
    // Back to the line's own scent: the rendered (default) choice; Swap hides again.
    [].forEach.call(panel.querySelectorAll('input[type="radio"]'), function (input) {
      input.checked = input.defaultChecked;
    });
    clearError(panel, 'monfiora-line__swap-error');
    syncSwap(panel);
    panel.hidden = true;
    var toggle = document.querySelector('[aria-controls="' + panel.id + '"]');
    if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus(); }
    queueOverflow();
  }

  function onSwap(btn) {
    var panel = btn.closest('.monfiora-line__change-panel');
    var line = lineOf(btn);
    if (!panel || !line) return;
    var picked = panel.querySelector('input[type="radio"]:checked:not(:disabled)');
    if (!picked) return;
    swapFree(line.getAttribute('data-line-key'), picked.value, { opener: btn }).catch(function () {});
  }

  function scrollTile(input) {
    var tile = input.closest('.monfiora-freetile');
    if (!tile || typeof tile.scrollIntoView !== 'function') return;
    try { tile.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (e) {}
  }

  // A strip radio changed (kept from Dawn's quantity handler by the capture guards).
  function onStripChange(input) {
    scrollTile(input);
    var panel = input.closest('.monfiora-line__change-panel');
    if (panel) {
      clearError(panel, 'monfiora-line__swap-error');
      syncSwap(panel);
    }
  }

  /* ---------- change scent: the swap ---------- */
  function findLine(root, key) {
    var lines = root.querySelectorAll('.monfiora-line');
    for (var i = 0; i < lines.length; i++) if (lines[i].getAttribute('data-line-key') === key) return lines[i];
    return null;
  }
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function holdWhile(promise) {
    var fg = window.MonfioraFreeGift;
    if (fg && typeof fg.hold === 'function') fg.hold(promise);
    return promise;
  }
  // A rate limit: a write's or a cart read's status, or a section read's "HTTP 429" (assets/cart-drawer.js).
  function is429(err) { return !!err && (err.status === 429 || (err.status == null && /^HTTP 429\b/.test(String(err.message || '')))); }
  // A free piece of the set: _free_gift 'true' and that _set_id. An empty setId matches a free piece with
  // none (lp-free-gift.js's own claim adds one without it).
  function isSetGift(it, setId) {
    var p = it && it.properties;
    return !!p && String(p._free_gift) === 'true' && String(p._set_id == null ? '' : p._set_id) === String(setId || '');
  }
  // The piece step 1 added, as a cart (or an add response) lists it.
  function addedLine(cart, variantId, setId) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === variantId && isSetGift(items[i], setId)) return items[i];
    }
    return null;
  }
  // The old piece as the cart lists it NOW. Once step 1 lands, the Buy 4 Get 1 allocation moves and
  // Shopify gives the old line a new key, so the key the surface rendered is stale (/cart/change.js would
  // answer 400): same variant, same set, still free, and not the line step 1 added.
  function oldLine(cart, oldId, oldSetId, newKey) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (String(it.variant_id) === oldId && it.key !== newKey && isSetGift(it, oldSetId)) return it;
    }
    return null;
  }
  // Free-piece lines the surface shows for any of these sets.
  function giftLinesShown(root, setIds) {
    if (!root) return 0;
    return [].filter.call(root.querySelectorAll('.monfiora-line--gift'), function (el) {
      return setIds.indexOf(el.getAttribute('data-set-id') || '') >= 0;
    }).length;
  }
  // A "couldn’t confirm" alert left while the cart showed the set with two free pieces carries the set's
  // ids. Once a read shows that set with one free piece again (lp-free-gift.js removed the charged one
  // after the hold), the cart is right and the alert is stale. On /cart it sits in #cart-errors, which
  // lp-free-gift.js's own render leaves alone.
  function clearSettledSwapErrors(cart) {
    if (swapping || !cart || !cart.items) return;
    [].forEach.call(document.querySelectorAll('.monfiora-line__swap-error[data-mf-set-id]'), function (n) {
      var ids = [n.getAttribute('data-mf-set-id') || '', n.getAttribute('data-mf-old-set-id') || ''];
      var units = 0;
      cart.items.forEach(function (it) {
        if (isSetGift(it, ids[0]) || isSetGift(it, ids[1])) units += Number(it.quantity) || 0;
      });
      if (units === 1) n.remove();
    });
  }

  // Add the new piece, then remove the old line: a failure before the remove leaves the cart as it was,
  // so a rate limit can never leave a set without its free piece. One render, from the remove's response.
  function swapFree(lineKey, newVariantId, opts) {
    opts = opts || {};
    var s = surface();
    var line = s && lineKey ? findLine(s.root, String(lineKey)) : null;
    if (!line) return Promise.resolve(null);
    var oldId = String(line.getAttribute('data-variant-id') || '');
    var newId = String(newVariantId || '');
    var panel = line.querySelector('.monfiora-line__change-panel');
    var strip = panel && panel.querySelector('[data-mf-strip]');
    var swapBtn = (panel && panel.querySelector('[data-mf-swap]')) || opts.opener || null;
    var keepBtn = panel && panel.querySelector('[data-mf-keep]');
    var actions = (panel && panel.querySelector('.monfiora-line__change-actions')) || swapBtn;
    var say = function (attr) { return panel ? panel.getAttribute(attr) || '' : ''; };
    if (swapping || isBusy(swapBtn) || !newId || newId === oldId || checkoutHeld()) return Promise.resolve(null);
    clearError(panel, 'monfiora-line__swap-error');
    clearError(s.errors(), 'monfiora-line__swap-error');   // an earlier swap's "couldn’t confirm" (/cart)
    if (claimsBlocked()) {
      // Shopify charged a recent claim: a new piece would come back charged too.
      showError(actions, 'monfiora-line__swap-error', say('data-mf-blocked'));
      return Promise.resolve(null);
    }
    swapping = true;
    var oldSetId = line.getAttribute('data-set-id') || '';
    var setId = oldSetId || newSetId();
    var host = null;
    var picked = strip ? strip.querySelector('input[value="' + newId.replace(/["\\]/g, '') + '"]') : null;

    function release() {
      if (host) host.classList.remove('cart__items--disabled');
      var h = linesHost();
      if (h) h.classList.remove('cart__items--disabled');
      if (swapBtn && swapBtn.isConnected) setBusy(swapBtn, false);
      if (strip && strip.isConnected) strip.removeAttribute('inert');
      if (keepBtn && keepBtn.isConnected) keepBtn.removeAttribute('inert');
      if (panel && panel.isConnected) panel.removeAttribute('aria-busy');
      swapping = false;
    }
    // Step 1 changed nothing: say so under the strip, which is still on screen.
    function unchanged(text) {
      release();
      if (actions && actions.isConnected) showError(actions, 'monfiora-line__swap-error', text);
      return null;
    }
    // Swap hid while it had focus: focus moves to el inside the strip (in the drawer, kept in its trap).
    function refocus(el) {
      var active = document.activeElement;
      if (!el || !el.isConnected) return;
      if (active && active !== document.body && active !== swapBtn && !(panel && panel.contains(active))) return;
      var d = s.kind === 'drawer' ? drawer() : null;
      if (d && typeof d.retrapOn === 'function') d.retrapOn(el); else focusEl(el);
    }
    // The outcome is not known (or the remove failed twice): show what the cart holds now, then say so.
    // Focus goes to the new line's Change scent (else its name) when that line is known.
    function unknown(newKey) {
      var text = say('data-mf-unknown');
      var where = newKey ? { focusKey: 'change-scent-' + newKey, fallback: 'line-' + newKey } : { focusKey: null };
      return s.refresh(Object.assign(where, { source: 'swap', announce: false })).catch(function () {}).then(function () {
        release();
        var now = surface();
        var alert = appendError(now && now.errors(), 'monfiora-line__swap-error', text);
        // The set shows two free pieces for now: the alert goes once a read shows one again.
        if (alert && now && giftLinesShown(now.root, [oldSetId, setId]) > 1) {
          alert.setAttribute('data-mf-set-id', setId);
          alert.setAttribute('data-mf-old-set-id', oldSetId);
        }
        return null;
      });
    }
    // Step 2: read the cart, then remove the old piece by its current key (the rendered lineKey went stale
    // when step 1 moved the allocation). Tried twice, 800 ms apart (2 s after a 429 on either request).
    // Already gone (an earlier remove landed, its answer lost) is success: the render shows it. The new
    // piece missing from the cart is not: the old one stays, and the cart is shown as saved.
    function removeOld(newKey, known) {
      var url = route('cart_change_url', '/cart/change') + '.js';
      var message = picked ? picked.getAttribute('data-mf-swapped') || '' : '';
      // The one render: focus on the new line's Change scent, else its name, by the key the server
      // lists now (it can move again as the old line goes), and the one "Free piece changed to …".
      function shown(cart) {
        var now = addedLine(cart, newId, setId);
        var key = (now && now.key) || newKey;
        return { focusKey: key ? 'change-scent-' + key : null, fallback: key ? 'line-' + key : undefined, message: message, source: 'swap' };
      }
      function attempt(cart) {
        return (cart ? Promise.resolve(cart) : readCart()).then(function (now) {
          if (!addedLine(now, newId, setId)) {
            var missing = new Error('the new piece is not in the cart');
            missing.final = true;
            throw missing;
          }
          var old = oldLine(now, oldId, oldSetId, newKey);
          if (!old) return s.refresh(shown(now));
          return send(url, { id: String(old.key), quantity: 0, sections: s.ids, sections_url: window.location.pathname })
            .then(function (json) { return render(json, shown(json)); });
        });
      }
      return attempt(known).catch(function (err) {
        if (err && err.final) throw err;
        return wait(is429(err) ? SWAP_RETRY_429_MS : SWAP_RETRY_MS).then(function () { return attempt(null); });
      }).then(function (result) {
        release();
        return result;
      }, function (err) {
        return unknown(err && err.final ? null : newKey);
      });
    }
    var sequence = whenLinesSettled().then(function () {
      host = s.host();
      if (host) host.classList.add('cart__items--disabled');   // Dawn and lp-free-gift.js wait meanwhile
      if (strip) strip.setAttribute('inert', '');
      if (keepBtn) keepBtn.setAttribute('inert', '');
      if (panel) panel.setAttribute('aria-busy', 'true');
      setBusy(swapBtn, true, say('data-mf-busy'));
      var item = { id: Number(newId), quantity: 1, properties: { _free_gift: 'true', _set_id: setId } };
      // Step 1: no sections; the only render comes from step 2.
      return send(route('cart_add_url', '/cart/add') + '.js', { items: [item] }).then(function (json) {
        var added = addedLine(json, newId, setId);
        return removeOld(added ? added.key : null);
      }, function (err) {
        var status = err && err.status;
        if (status === 422) {
          // Refused (sold out): the tile can no longer be picked; the line's own scent is checked again
          // ("Your free {design} · {scent} is still in your cart."), so Swap hides and focus goes to that
          // tile, else Keep.
          if (picked) markSoldOut(picked);
          if (strip) [].forEach.call(strip.querySelectorAll('input[type="radio"]'), function (r) { if (r.defaultChecked) r.checked = true; });
          if (panel) syncSwap(panel);
          var refused = unchanged(say('data-mf-refused'));
          refocus((strip && strip.querySelector('input[type="radio"]:checked:not(:disabled)')) || keepBtn);
          return refused;
        }
        if (status && status !== 200 && status < 500) return unchanged(say('data-mf-fail'));   // 429 and other refusals
        // A 5xx, an unreadable answer or a lost connection: the add may have landed. Read the cart (step 2
        // starts from this read).
        return readCart().then(function (cart) {
          var added = addedLine(cart, newId, setId);
          if (added) return removeOld(added.key, cart);
          return unchanged(say('data-mf-fail'));
        }, function () { return unknown(null); });
      });
    });
    return holdWhile(sequence.catch(function (err) {
      release();
      throw err;
    }));
  }

  /* ---------- drawer footer shadow: content hidden below the pinned footer ---------- */
  var overflowOn = false;       // the last state, carried onto a freshly rendered footer before measuring
  var overflowFrame = 0;
  var overflowObserver = null;
  var observedBody = null;

  // A render replaces the body: observe the new one and its direct children (a strip opening, an error
  // or a notice changes their height, not the body's own box).
  function watchBody(body) {
    if (body === observedBody || typeof window.ResizeObserver !== 'function') return;
    if (!overflowObserver) overflowObserver = new window.ResizeObserver(function () { queueOverflow(); });
    overflowObserver.disconnect();
    observedBody = body || null;
    if (!body) return;
    overflowObserver.observe(body);
    [].forEach.call(body.children, function (child) { overflowObserver.observe(child); });
  }

  function syncOverflow() {
    overflowFrame = 0;
    var d = document.querySelector('cart-drawer');
    var body = d && d.querySelector('.monfiora-drawer__body');
    var footer = d && d.querySelector('.monfiora-drawer__footer');
    watchBody(body);
    if (!footer) { overflowOn = false; return; }
    // A new footer starts with the last state, so a render that changes nothing does not fade it again.
    footer.toggleAttribute('data-overflow', overflowOn);
    overflowOn = !!body && body.scrollHeight - body.clientHeight - body.scrollTop > 1;
    footer.toggleAttribute('data-overflow', overflowOn);
  }

  function queueOverflow() {
    if (overflowFrame) return;
    overflowFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(syncOverflow)
      : setTimeout(syncOverflow, 16);
  }

  function onClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;
    var btn;
    if ((btn = t.closest('[data-mf-claim]'))) { evt.preventDefault(); onClaim(btn); return; }
    if ((btn = t.closest('[data-mf-suggest-add]'))) { evt.preventDefault(); onSuggest(btn); return; }
    if ((btn = t.closest('[data-mf-remove-line]'))) { evt.preventDefault(); onRemove(btn); return; }
    if ((btn = t.closest('[data-mf-change]'))) { evt.preventDefault(); onChangeToggle(btn); return; }
    if ((btn = t.closest('[data-mf-keep]'))) { evt.preventDefault(); onKeep(btn); return; }
    if ((btn = t.closest('[data-mf-swap]'))) { evt.preventDefault(); onSwap(btn); }
  }

  // The surface's root: taps on a stepper (the swap and the claim wait for Dawn's pending change), the
  // delegated controls and the strip radios.
  function bindRoot(root, guardStrip) {
    ['click', 'input', 'change'].forEach(function (type) {
      root.addEventListener(type, function (evt) {
        var t = evt.target;
        if (evt.isTrusted && t && t.closest && t.closest('.monfiora-line__qty')) lastQtyTouch = Date.now();
      }, true);
    });
    root.addEventListener('click', onClick);
    root.addEventListener('change', function (evt) {
      var t = evt.target;
      if (!t || !t.closest || !t.closest('[data-mf-strip]')) return;
      if (guardStrip) evt.stopPropagation();
      onStripChange(t);
    }, true);
  }

  function init() {
    var d = document.querySelector('cart-drawer');
    if (d) {
      // assets/cart-drawer.js already stops a strip radio's change on <cart-drawer>.
      bindRoot(d, false);
      d.addEventListener('scroll', function (evt) {
        var t = evt.target;
        if (t && t.classList && t.classList.contains('monfiora-drawer__body')) queueOverflow();
      }, { capture: true, passive: true });
      // Opening (class "active") and emptying (class "is-empty") re-check the footer.
      if (typeof window.MutationObserver === 'function') {
        new window.MutationObserver(function () { queueOverflow(); }).observe(d, { attributes: true, attributeFilter: ['class'] });
      }
    } else {
      var page = pageRoot();
      if (page) {
        bindRoot(page, true);
        lastPageState = pageState(page);
        ['click', 'keydown', 'change'].forEach(function (type) {
          page.addEventListener(type, function (evt) {
            if (!evt.isTrusted) return;
            notePageIntent(evt.target);
            lastPageState = pageState(page);   // what a Dawn render then compares with
          }, true);
        });
        // Dawn's pub/sub is a lexical global (assets/pubsub.js, constants.js); a throw here would reach
        // Dawn's catch and show its error line, so it never throws.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
            try { onDawnRender(page, event); } catch (e) {}
          });
        }
      }
    }
    // Every drawer render path ends here (assets/cart-drawer.js afterRender): checked in the same task as
    // the swap, so the new footer never paints without its state.
    document.addEventListener('monfiora:cart:updated', syncOverflow);
    // lp-free-gift.js says what every read showed; a swap alert the cart has since settled goes.
    document.addEventListener('lpfg:state', function (evt) {
      try { clearSettledSwapErrors(evt && evt.detail && evt.detail.cart); } catch (e) {}
    });
    window.addEventListener('resize', queueOverflow);
    queueOverflow();
    if (document.body) document.body.addEventListener('modalClosed', function () { setTimeout(syncLock, 0); });
    window.addEventListener('pagehide', function () {
      if (document.body) document.body.classList.remove('overflow-hidden');
    });
    window.addEventListener('pageshow', function (evt) {
      if (evt && evt.persisted) {
        busyButtons.slice().forEach(function (b) { setBusy(b, false); });
        swapping = false;
        // A restore mid-request leaves no re-render behind: the pickers, Keep and suggestion rows take taps again.
        [].forEach.call(document.querySelectorAll('[data-mf-strip][inert], [data-mf-suggest-row][inert], [data-mf-keep][inert]'),
          function (n) { n.removeAttribute('inert'); });
        [].forEach.call(document.querySelectorAll('.monfiora-line__change-panel[aria-busy]'), function (n) { n.removeAttribute('aria-busy'); });
      }
      syncLock();
      var open = drawer();
      if (open && open.classList.contains('active') && typeof open.retrap === 'function') open.retrap();
    });
  }

  window.MonfioraCart = {
    sectionIds: sectionIds,
    openDrawer: openDrawer,
    refresh: refresh,
    add: add,
    claimFree: claimFree,
    removeLine: removeLine,
    swapFree: swapFree,
    note: note,
    isOpen: isOpen,
    syncLock: syncLock
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
